import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect, useCallback, useRef } from "react";
import {
  fetchIndividualAppointments,
  fetchIndividualAppointmentFilterOptions,
  setPage,
  setPageSize,
  setSearch,
  setServiceParent,
  setSortKey,
  setStatus,
  setDateFrom,
  setDateTo,
  updateAppointmentInList,
} from "./appointments.slice";
import { useDebounce } from "@/hooks/useDebounce";
import type { AppointmentJoin } from "@/repositories/appointments.repository";

export const useIndividualAppointments = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s) => s.individualAppointmentsNew);
  const abortControllerRef = useRef<AbortController | null>(null);

  const debouncedSearch = useDebounce(state.filters.search, 400);

  useEffect(() => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const fetchData = async () => {
      try {
        await dispatch(fetchIndividualAppointments(debouncedSearch)).unwrap();
      } catch (error: any) {
        if (error.message !== "Request cancelled") {
          console.error("Failed to fetch appointments:", error);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    dispatch,
    state.filters.status,
    state.filters.serviceParent,
    debouncedSearch, // Use debounced search for API calls - this prevents requests on every keystroke
    state.filters.dateFrom,
    state.filters.dateTo,
    state.filters.sortKey,
    state.filters.sortOrder,
    state.pagination.page,
    state.pagination.pageSize,
  ]);

  useEffect(() => {
    dispatch(fetchIndividualAppointmentFilterOptions());
  }, [dispatch]);

  // Memoize filter setters to prevent unnecessary re-renders
  const setStatusCallback = useCallback((v: string) => dispatch(setStatus(v)), [dispatch]);
  const setServiceParentCallback = useCallback((v: string) => dispatch(setServiceParent(v)), [dispatch]);
  const setSearchCallback = useCallback((v: string) => dispatch(setSearch(v)), [dispatch]);
  const setDateFromCallback = useCallback((v: string) => dispatch(setDateFrom(v)), [dispatch]);
  const setDateToCallback = useCallback((v: string) => dispatch(setDateTo(v)), [dispatch]);
  const setSortKeyCallback = useCallback((k: string) => dispatch(setSortKey(k)), [dispatch]);
  const setPageCallback = useCallback((p: number) => dispatch(setPage(p)), [dispatch]);
  const setPageSizeCallback = useCallback((ps: number) => dispatch(setPageSize(ps)), [dispatch]);
  const updateAppointmentInListCallback = useCallback(
    (appointment: AppointmentJoin) => dispatch(updateAppointmentInList(appointment)),
    [dispatch]
  );

  const refreshAppointments = useCallback(async () => {
    try {
      await dispatch(fetchIndividualAppointments(undefined)).unwrap();
    } catch (error: any) {
      if (error.message !== "Request cancelled") {
        console.error("Failed to refresh appointments:", error);
      }
    }
  }, [dispatch]);

  return {
    list: state.list,
    total: state.total,
    loading: state.loading,
    options: state.options,

    filters: {
      ...state.filters,
      setStatus: setStatusCallback,
      setServiceParent: setServiceParentCallback,
      setSearch: setSearchCallback,
      setDateFrom: setDateFromCallback,
      setDateTo: setDateToCallback,
      setSortKey: setSortKeyCallback,
    },

    pagination: {
      ...state.pagination,
      setPage: setPageCallback,
      setPageSize: setPageSizeCallback,
    },

    // actions
    updateAppointmentInList: updateAppointmentInListCallback,
    refreshAppointments,

    dispatch,
  };
};
